/**
 * A button.
 *
 * `asChild` renders the styling onto a child element instead of a `<button>`,
 * which is how a router link gets a button's appearance without nesting an
 * anchor inside a button — invalid markup that also breaks keyboard behaviour.
 *
 * @see docs/plan/IMPLEMENTATION_PLAN.md task 5.6
 */
import { Slot } from '@radix-ui/react-slot';

import { cn } from '../../lib/cn';

import { buttonVariants, type ButtonVariants } from './button-variants';

export interface ButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement>, ButtonVariants {
  /** Render the styling onto the child element rather than a `<button>`. */
  asChild?: boolean;
}

export function Button({
  className,
  variant,
  size,
  asChild = false,
  type = 'button',
  ...props
}: ButtonProps): React.JSX.Element {
  const Component = asChild ? Slot : 'button';

  return (
    <Component
      /* Defaulted to `button`: an unspecified type inside a form is `submit`,
         which turns every incidental button into an accidental submission. */
      {...(asChild ? {} : { type })}
      className={cn(buttonVariants({ variant, size }), className)}
      {...props}
    />
  );
}
